import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, InputSignal, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MaintenanceComponent } from '@libs/common/components';
import { BreadcrumbComponent } from '@libs/components/navigation';
import { CepStateService } from '@libs/shared/common';
import { CapabilityService } from '@libs/shared/services';
import { AppError, NavItemModel } from '@libs/shared/models';
import { PageViewService } from '@libs/shared/services';
import { DeferredPageInfoTracking } from '@libs/shared/tracking';
import { CatalogSearchRouteComponent } from '@libs/shop/components';
import { filter, first, Observable } from 'rxjs';
import { ContentPageRouteComponent } from './route';

@Component({
  selector: 'ui-content-page',
  templateUrl: './content-page.component.html',
  styleUrls: ['./content-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    BreadcrumbComponent,
    ContentPageRouteComponent,
    CatalogSearchRouteComponent,
    MaintenanceComponent,
  ],
  providers: [DeferredPageInfoTracking],
})
export class ContentPageComponent {
  public readonly cepStateService: CepStateService = inject(CepStateService);
  public readonly pageViewService: PageViewService = inject(PageViewService);

  public page: InputSignal<string | undefined> = input();
  public ipp: InputSignal<string | undefined> = input();
  public label: InputSignal<string | undefined> = input();
  public sort: InputSignal<string | undefined> = input();
  public manufacturer: InputSignal<string | undefined> = input();
  public own_brand: InputSignal<string | undefined> = input();

  public title: Signal<string | undefined> = this.cepStateService.pageTitle;
  public tree: Signal<NavItemModel | undefined> = toSignal(this.pageViewService.getTree());
  public current: Signal<NavItemModel | undefined> = toSignal(this.pageViewService.getCurrentNode());
  public error: Signal<AppError | undefined> = toSignal(this.pageViewService.getError());
  public breadcrumb: Signal<NavItemModel[]> = toSignal(
    this.pageViewService.getBreadcrumbNodes(),
    { initialValue: [] },
  );

  public readonly shouldUsePathBasedContentRoute: Signal<boolean> = computed(() => this.capabilityService.isAvailable('platform.cmsAppConfigMode')());

  private readonly capabilityService: CapabilityService = inject(CapabilityService);
  private readonly router: Router = inject(Router);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly deferredPageInfoTracking: DeferredPageInfoTracking = inject(DeferredPageInfoTracking);

  public constructor() {
    this.updatePageViewPath();
    this.completePageInfo();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe(
      () => {
        this.updatePageViewPath();
        this.completePageInfo();
      },
    );
  }

  public updatePageViewPath(): void {
    this.pageViewService.setPath(decodeURI(this.router.routerState.snapshot.url));
  }

  /**
   * Reports the page to pageInfo tracking once it is known what the page shows.
   * With a routed child page, that is once the navigation tree is loaded. Otherwise it is once the current path resolves
   * to a navigation node, because only then can a category page redirect to its search URL.
   */
  private completePageInfo(): void {
    const pageKnown$: Observable<unknown> = this.activatedRoute.firstChild
      ? this.pageViewService.whenNavigationTreeLoaded()
      : this.pageViewService.getCurrentNode().pipe(first(node => node !== undefined));

    pageKnown$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => void this.deferredPageInfoTracking.complete());
  }
}
